import { json, LoaderFunction, MetaFunction } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { Layout } from '~/components/Layout';
import { Container } from '~/components/Container';
import { PageHeading } from '~/components/PageHeading';
import { PedalList } from '~/components/PedalList';
import { Search } from '~/components/Search';
import { searchPedals } from '~/lib/pgvector.server';
import { getPager, Pagination } from '~/components/Pagination';
import { PaginationSummary } from '~/components/PaginationSummary';
import { getImagePublicUrl } from '~/lib/supabase.server';
import { SearchXIcon } from 'lucide-react';
import { appendMetaTitleSuffix } from '~/lib/utils/meta';
import { db } from '~/lib/db.server';
import { getCacheControlHeader } from '~/lib/http.server';

export const loader: LoaderFunction = async ({ request }) => {
  const { searchParams, pathname } = new URL(request.url);
  const paramTagsString = searchParams.get('tags');
  const paramTags = paramTagsString ? paramTagsString.split(',') : [];
  const paramBrandsString = searchParams.get('brands');
  const paramBrands = paramBrandsString ? paramBrandsString.split(',') : [];
  const paramQuery = searchParams.get('query');

  const pager = getPager({
    pathname,
    searchParams,
    perPage: 36
  });

  const [searchResult, brands, tags] = await Promise.all([
    searchPedals(
      paramQuery,
      paramTags,
      paramBrands,
      pager.perPage,
      pager.offset,
    ),
    db.brand.findMany({
      orderBy: {
        name: 'asc',
      },
    }),
    db.tag.findMany({
      orderBy: {
        order: 'asc',
      },
    }),
  ]);

  const imageUrlPrefix = getImagePublicUrl(request);

  const filteredBrandNames = brands.filter((brand) => paramBrands.includes(brand.id)).map((brand) => brand.name);
  const filteredTags = tags.filter((tag) => paramTags.includes(tag.id)).map((tag) => `#${tag.tag}`);
  let metaTitle = 'エフェクター';
  const metaComponents = [];
  if (paramQuery) {
    metaComponents.push(paramQuery);
  }
  if (filteredBrandNames.length > 0) {
    metaComponents.push(...filteredBrandNames);
  }
  if (filteredTags.length > 0) {
    metaComponents.push(...filteredTags);
  }

  if (metaComponents.length > 0) {
    metaTitle = `${metaComponents.join(' ')}の検索結果`;
  }
  if (pager.currentPage > 1) {
    metaTitle = `${metaTitle} - ${pager.currentPage}ページ目`
  }

  let metaDescription = 'エフェクターの新着一覧です。';
  if (metaComponents.length > 0) {
    metaDescription = `${metaComponents.join(' ')}に該当するエフェクターの検索結果です。`;
  }
  metaDescription = `${metaDescription}キーワードやタグ・ブランドのタグで絞り込んで好みのエフェクターを探すことができます。`

  return json(
    {
      searchResult,
      brands,
      tags,
      paramTags,
      paramBrands,
      paramQuery,
      pager,
      imageUrlPrefix,
      metaTitle,
      metaDescription,
    },
    {
      headers: {
        ...getCacheControlHeader()
      }
    }
  );
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { title: appendMetaTitleSuffix(data.metaTitle) },
    { name: 'description', content: data.metaDescription },
  ];
};

const PedalsRoute = () => {
  const { searchResult, brands, tags, paramTags, paramBrands, paramQuery, pager, imageUrlPrefix } =
    useLoaderData<typeof loader>();

  return (
    <Layout>
      <Container>
        <PageHeading>エフェクター</PageHeading>

        <Search brands={brands} tags={tags} initialQuery={paramQuery} initialBrands={paramBrands} initialTags={paramTags} />

        {searchResult.total > 0 && (
          <>
            <div className='mt-6'>
              <PaginationSummary
                total={searchResult.total}
                offset={pager.offset}
                perPage={pager.perPage}
              />
            </div>

            <div className={'grid lg:grid-cols-3 md:grid-cols-1 gap-6 mt-3'}>
              <PedalList pedals={searchResult.pedals} imageUrlPrefix={imageUrlPrefix} />
            </div>

            <div className='mt-6'>
              <Pagination total={searchResult.total} pager={pager} />
            </div>
          </>
        )}


        {searchResult.total < 1 && (
          <div className='mt-16 flex flex-col justify-center items-center text-slate-300'>
            <SearchXIcon className="h-20 w-20" />
            <div className={'text-black font-bold mt-2 text-lg'}>
              エフェクターがみつかりませんでした
            </div>
            <div className={'text-slate-400 mt-1 text-sm'}>
              条件を変えて検索してみてください
            </div>
           </div>
        )}

      </Container>
    </Layout>
  );
};

export default PedalsRoute;
